import { useState } from 'react'
import toast from 'react-hot-toast'

import { Button } from '@/components/carbon/Button'
import { IconCopy } from '@/components/carbon/icons'
import { Tag } from '@/components/carbon/Tag'

/**
 * Matches the RemediationFix contract (generate-the-fix blueprint §4.1), plus
 * 'Manual' — a frontend-only label for Rego-authored prose whose channel we
 * don't actually know (portal path, DNS record, process step). Guessing
 * "Admin center" for a DNS registrar change sends the admin to the wrong
 * console; 'Manual' stays truthful.
 */
export type FixType = 'PowerShell' | 'GraphApi' | 'AzCli' | 'AdminCenter' | 'NotSupported' | 'Manual'

const FIX_TYPE_LABEL: Record<FixType, string> = {
  PowerShell: 'PowerShell',
  GraphApi: 'Graph API',
  AzCli: 'az CLI',
  AdminCenter: 'Admin center',
  NotSupported: 'Not supported',
  Manual: 'Manual step',
}

/** Command channels render mono; manual/portal steps render as prose. */
const CODE_FIX_TYPES: ReadonlySet<FixType> = new Set(['PowerShell', 'GraphApi', 'AzCli'])

interface FixCommandBlockProps {
  fixType: FixType
  /** The command text; for AdminCenter, the click path rendered as steps. */
  command: string
  prerequisites?: string
  verifyCommand?: string
  notes?: string
  /** Toggles the disclaimer variant: AI-drafted (drift) vs Rego-authored (BPA). */
  aiGenerated: boolean
  /** The drift changed since this fix was drafted (AI variant only). */
  stale?: boolean
  onRegenerate?: () => void
  /** Hide the copy button — used for prose-only click paths from Rego. */
  copyable?: boolean
}

function copyToClipboard(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast.success('Copied. Review before you run it.'),
    () => toast.error('Could not copy. Select and copy manually.'),
  )
}

/**
 * The shared fix-as-text block (generate-the-fix blueprint §5 A-1) on the
 * Carbon tokens: an outline tag naming the channel, the copy button, the
 * command in IBM Plex Mono (code and IDs keep a mono, DESIGN.md
 * "Typography"), optional prerequisites / verify / notes, and the standing
 * "review before you run" disclaimer. Square, layer-01, no shadow.
 * Guardian never executes anything — this block only hands the admin a
 * prepared move to review.
 */
export default function FixCommandBlock({
  fixType,
  command,
  prerequisites,
  verifyCommand,
  notes,
  aiGenerated,
  stale,
  onRegenerate,
  copyable = true,
}: FixCommandBlockProps) {
  const [showVerify, setShowVerify] = useState(false)
  const isCode = CODE_FIX_TYPES.has(fixType)

  return (
    <div className="min-w-0 border border-line-subtle bg-layer-1 text-body">
      {/* Header — the channel tag left, copy affordance right */}
      <div className="flex min-h-10 items-center justify-between gap-3 border-b border-line-subtle pl-4">
        <Tag kind="outline" size="sm">
          {FIX_TYPE_LABEL[fixType]}
        </Tag>
        {copyable && (
          <Button kind="ghost" size="sm" icon={<IconCopy />} onClick={() => copyToClipboard(command)}>
            Copy
          </Button>
        )}
      </div>

      {/* Staleness — the drift moved on since this was drafted */}
      {stale && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-subtle bg-layer-2 px-4 py-2">
          <p className="m-0 text-helper text-text-primary">The drift changed since this was drafted.</p>
          {onRegenerate && (
            <Button kind="ghost" size="sm" onClick={onRegenerate}>
              Redraft
            </Button>
          )}
        </div>
      )}

      {prerequisites && (
        <p className="m-0 border-b border-line-subtle px-4 py-2 text-helper text-text-secondary">
          <span className="mr-2 font-semibold text-text-primary">Before you start</span>
          {prerequisites}
        </p>
      )}

      {/* The fix itself. Commands get the mono code treatment; manual and
          portal steps read as prose, since mono styling on English text
          falsely signals "run this". */}
      {isCode ? (
        <pre className="m-0 max-h-64 overflow-auto px-4 py-3 text-[13px] leading-[1.43]">
          <code className="block whitespace-pre-wrap break-words font-mono text-text-primary">{command}</code>
        </pre>
      ) : (
        <p className="m-0 max-h-64 overflow-auto whitespace-pre-wrap px-4 py-3 text-body text-text-primary">
          {command}
        </p>
      )}

      {verifyCommand && (
        <div className="border-t border-line-subtle px-4 py-2">
          <Button
            kind="ghost"
            size="sm"
            aria-expanded={showVerify}
            onClick={() => setShowVerify(!showVerify)}
            className="-ml-3"
          >
            {showVerify ? 'Hide the check' : 'Check current state first'}
          </Button>
          {showVerify && (
            <pre className="m-0 mt-2 max-h-40 overflow-auto bg-layer-2 px-3 py-2 text-[13px] leading-[1.43]">
              <code className="block whitespace-pre-wrap break-words font-mono text-text-secondary">
                {verifyCommand}
              </code>
            </pre>
          )}
        </div>
      )}

      {notes && (
        <p className="m-0 border-t border-line-subtle px-4 py-2 text-body text-text-secondary">
          {notes}
        </p>
      )}

      {/* Standing disclaimer — always visible, never dismissible (blueprint §2.1) */}
      <p className="m-0 border-t border-line-subtle px-4 py-2.5 text-helper text-text-helper">
        {aiGenerated ? (
          <>
            <span className="font-semibold text-text-primary">Guardian drafts the fix; you decide.</span> This command
            was generated by AI from the drift&rsquo;s recorded values and has not been run or
            validated against your tenant. Review it before running, confirm it fits your change
            process, and test in a non-production scope where you can. Guardian never executes
            changes to your environment.
          </>
        ) : (
          <>
            <span className="font-semibold text-text-primary">A starting point, not a script Guardian ran.</span>{' '}
            Verify this against your tenant&rsquo;s current state and your change process before
            running it. Guardian never executes changes to your environment.
          </>
        )}
      </p>
    </div>
  )
}
